import type { NotificationType } from '@yawi/domain';

/**
 * Maps a notification to a web route and navigates to it.
 *
 * Resolution order:
 *  1. `data.screen` (deep-link spec from the backend).
 *  2. Fallback: `notification.type` mapped to the nearest list screen.
 */
export function getRouteFromNotification(
  data: Record<string, unknown> | null | undefined,
  type?: NotificationType
): string | null {
  const screen = typeof data?.screen === 'string' ? data.screen : undefined;

  if (screen) {
    const route = getRouteForScreen(screen);
    if (route) return route;
  }

  if (type) {
    const route = getRouteForNotificationType(type);
    if (route) return route;
  }

  return null;
}

function getRouteForScreen(screen: string): string | null {
  switch (screen) {
    case 'ChargeDetail':
    case 'PaymentDetail':
      return '/pagos';
    case 'GuestList':
    case 'VisitorDetail':
      return '/accesos';
    case 'UnitDetail':
    case 'VehicleDetail':
    case 'PetDetail':
    case 'GdprExport':
      return '/perfil';
    case 'AmenityDetail':
      return '/amenidades';
    case 'DocumentDetail':
      return '/documentos';
    case 'PollResults':
    case 'PollDetail':
      return '/encuestas';
    case 'TicketDetail':
    case 'IncidentDetail':
      return '/incidentes';
    case 'AnnouncementDetail':
      return '/comunicados';
    case 'ProjectDetail':
      return '/transparencia';
    case 'AccessHistory':
      return '/registro-accesos';
    case 'ReservationDetail':
      return '/amenidades';
    default:
      return null;
  }
}

function getRouteForNotificationType(type: NotificationType): string | null {
  // Payments / charges / invoices / deposits
  if (
    type.startsWith('PAYMENT_') ||
    type === 'INVOICE_GENERATED' ||
    type === 'CHARGE_CANCELLED' ||
    type === 'PROOF_RECEIVED' ||
    type === 'DEPOSIT_REFUNDED' ||
    type === 'DEPOSIT_DEDUCTED'
  ) {
    return '/pagos';
  }

  // Access events tied to a guest/invitation arrival. Estas notificaciones son para
  // RESIDENT/FAMILIAR (avisan de sus propios invitados) — van a /accesos (su historial
  // propio), no a /registro-accesos (la tabla completa, solo ADMIN/GUARD).
  if (type === 'GUEST_ARRIVED' || type === 'INVITATION_USED') {
    return '/accesos';
  }

  // Other guest / invitation lifecycle events
  if (type.startsWith('GUEST_') || type.startsWith('INVITATION_')) {
    return '/accesos';
  }

  // Access control / device / restrictions / unit membership
  if (
    type === 'ACCESS_DENIED_BLACKLIST' ||
    type.startsWith('DEVICE_') ||
    type.startsWith('RESTRICTION_') ||
    type.startsWith('UNIT_MEMBER_')
  ) {
    return '/accesos';
  }

  // Reservations / amenities
  if (type.startsWith('RESERVATION_') || type.startsWith('AMENITY_')) {
    return '/amenidades';
  }

  // Support tickets / incidents
  if (type.startsWith('TICKET_') || type.startsWith('INCIDENT_')) {
    return '/incidentes';
  }

  // Announcements / events
  if (type.startsWith('ANNOUNCEMENT_') || type === 'EVENT_REMINDER') {
    return '/comunicados';
  }

  // Documents / GDPR
  if (type === 'DOCUMENT_UPLOADED' || type === 'GDPR_EXPORT_READY') {
    return '/documentos';
  }

  // Polls
  if (type.startsWith('POLL_')) {
    return '/encuestas';
  }

  // Transparency
  if (type.startsWith('TRANSPARENCY_')) {
    return '/transparencia';
  }

  // Pets, vehicles, household
  if (
    type.startsWith('PET_') ||
    type.startsWith('VACCINE_') ||
    type.startsWith('VEHICLE_') ||
    type.startsWith('HOUSEHOLD_')
  ) {
    return '/perfil';
  }

  // User / security
  if (
    type.startsWith('USER_') ||
    type === 'PASSWORD_CHANGED' ||
    type === 'NEW_DEVICE_LOGIN'
  ) {
    return '/perfil';
  }

  return null;
}
